import { DatePicker } from '@/app/common/components/datepicker'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { useUpdateProgramPurseAcademicTerm } from '@/queries/academic_terms/mutations'
import { ProgramTermsArray } from '@/queries/academic_terms/schema'
import { ProgramYearsProps } from '@/queries/program/schemas'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { addDays, format } from 'date-fns'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { AwardDatesSchema } from './select-award-dates.schema'

export type AwardDatesOptions = z.infer<ReturnType<typeof AwardDatesSchema>>

type Props = {
  selectedAwardPeriodId: number
  onOpenChange: (open: boolean) => void
  programAcademicTerms: ProgramTermsArray
  programSettings: ProgramYearsProps
  purseId: number
}

export default function SetTermStartAndEndDateForm(props: Props) {
  const {
    selectedAwardPeriodId,
    onOpenChange,
    programSettings,
    programAcademicTerms,
    purseId,
  } = props

  const { mutate: updateProgramAcademicTerm, status } =
    useUpdateProgramPurseAcademicTerm(programSettings.id, purseId)

  function onAwardDatesSubmit(data: AwardDatesOptions) {
    updateProgramAcademicTerm(
      {
        programId: programSettings.id,
        purseId: purseId,
        academicTermId: selectedAwardPeriodId,
        startDate: format(data.startDate, 'yyyy-MM-dd'),
        endDate: format(data.endDate, 'yyyy-MM-dd'),
        schoolYear: `${data.startDate.getFullYear()}-${data.endDate.getFullYear()}`,
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  const selectedAwardPeriod = programAcademicTerms.find(
    (period) => period.id === selectedAwardPeriodId
  )

  function getPreviousSameTypeTerm() {
    if (!selectedAwardPeriod) return null
    const sameTypeTerms = programAcademicTerms
      .filter(
        (t) =>
          t.academic_term.academic_term_type_id ===
          selectedAwardPeriod.academic_term.academic_term_type_id
      )
      .sort((a, b) => a.academic_term.term_number - b.academic_term.term_number)
    const currentIndex = sameTypeTerms.findIndex(
      (t) => t.id === selectedAwardPeriodId
    )
    return currentIndex > 0 ? sameTypeTerms[currentIndex - 1] : null
  }

  function getStartDateMinDate(): Date {
    const prevTerm = getPreviousSameTypeTerm()
    if (prevTerm?.awarding_period_end_date) {
      return addDays(new Date(prevTerm.awarding_period_end_date), 1)
    }
    return new Date('2015-01-01')
  }

  function getDefaultValues() {
    if (
      selectedAwardPeriod?.awarding_period_start_date &&
      selectedAwardPeriod?.awarding_period_end_date
    ) {
      return {
        startDate: new Date(selectedAwardPeriod.awarding_period_start_date),
        endDate: new Date(selectedAwardPeriod.awarding_period_end_date),
      }
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const minStart = getStartDateMinDate()
    const defaultStart = minStart > today ? minStart : today
    return {
      startDate: defaultStart,
      endDate: addDays(defaultStart, 1),
    }
  }

  const awardDatesForm = useForm<AwardDatesOptions>({
    resolver: zodResolver(
      AwardDatesSchema(programAcademicTerms, selectedAwardPeriodId)
    ),
    defaultValues: getDefaultValues(),
  })

  const hasAddedDates =
    selectedAwardPeriod?.awarding_period_start_date &&
    selectedAwardPeriod?.awarding_period_end_date

  return (
    <Dialog open={true} onOpenChange={onOpenChange} key={selectedAwardPeriodId}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {hasAddedDates ? 'Edit' : 'Add'} {selectedAwardPeriod?.name} Award
            Dates
          </DialogTitle>
          <p className="text-gray-600 text-sm">
            Set the start and end dates for when awards (scholarships, grants,
            financial aid) can be distributed during this term
          </p>
        </DialogHeader>
        <Form {...awardDatesForm}>
          <form
            className="space-y-4 "
            onSubmit={awardDatesForm.handleSubmit(onAwardDatesSubmit)}
          >
            <FormField
              name="startDate"
              control={awardDatesForm.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      side="top"
                      minDate={getStartDateMinDate()}
                      allowFutureDates
                      name="startDate"
                      initialDate={new Date(field.value)}
                      onDateChange={(date) => {
                        field.onChange(date)
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="endDate"
              control={awardDatesForm.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <FormControl>
                    <DatePicker
                      name="endDate"
                      side="top"
                      initialDate={new Date(field.value)}
                      minDate={addDays(
                        awardDatesForm.watch('startDate') ?? new Date(),
                        1
                      )}
                      allowFutureDates
                      onDateChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="pt-10">
              <SubmitButton
                status={status}
                size={'lg'}
                type="submit"
                buttonTxt="Save Award Dates"
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
